import { useState, useRef, useMemo, useEffect } from "react";
import { RocketLaunch, DownloadSimple, UploadSimple, Plus, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    buildCampaignCsv,
    campaignFilename,
    downloadCsv,
    parseKeywordText,
    computeKeywordPools,
} from "@/lib/csv";
import { sanitizeAdAsset } from "@/lib/sanitizeAsset";
import { TrustCopyPanel } from "@/components/TrustCopyPanel";

const KwCount = ({ label, list }) => (
    <div className="flex items-center justify-between border border-neutral-300 bg-white px-3 py-2">
        <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
            {label}
        </span>
        <span className="font-heading text-base font-black text-ink">
            {list?.length || 0}
        </span>
    </div>
);

export const CampaignBuilderDialog = ({ result, refinedKeywords = [] }) => {
    const [open, setOpen] = useState(false);
    const [businessName, setBusinessName] = useState("");
    const [websiteUrl, setWebsiteUrl] = useState("");
    const [dailyBudget, setDailyBudget] = useState("");
    const [extraSeedsText, setExtraSeedsText] = useState("");
    const [customText, setCustomText] = useState("");
    const [manualKw, setManualKw] = useState("");
    const [customKeywords, setCustomKeywords] = useState([]);
    const fileInputRef = useRef(null);
    const [trustCopy, setTrustCopy] = useState({
        headlines: [],
        descriptions: [],
        callouts: [],
    });

    // When the user clicks "Apply to Builder" in the refinement panel, replace
    // the custom keyword chip list with the refined set so CSV export uses it.
    useEffect(() => {
        if (refinedKeywords && refinedKeywords.length) {
            setCustomKeywords(refinedKeywords);
        }
    }, [refinedKeywords]);

    // Merge textarea + manual chip list, then dedupe case-insensitively.
    const mergedCustom = useMemo(() => {
        const fromText = parseKeywordText(customText);
        const combined = [...customKeywords, ...fromText];
        const seen = new Set();
        const out = [];
        for (const k of combined) {
            const key = k.toLowerCase().trim();
            if (!key || seen.has(key)) continue;
            seen.add(key);
            out.push(k.trim());
        }
        return out;
    }, [customText, customKeywords]);

    const handleFileUpload = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            const parsed = parseKeywordText(ev.target.result);
            if (!parsed.length) {
                toast.error("No keywords found in file");
                return;
            }
            // Merge into chip list so text field stays clean for further paste.
            setCustomKeywords((prev) => {
                const seen = new Set(prev.map((k) => k.toLowerCase()));
                const next = [...prev];
                for (const k of parsed) {
                    const key = k.toLowerCase();
                    if (!seen.has(key)) {
                        seen.add(key);
                        next.push(k);
                    }
                }
                return next;
            });
            toast.success(`Imported ${parsed.length} keyword${parsed.length === 1 ? "" : "s"} from ${file.name}`);
        };
        reader.onerror = () => toast.error("Could not read file");
        reader.readAsText(file);
        // reset so the same file can be re-uploaded
        e.target.value = "";
    };

    const addManualKeyword = () => {
        const s = manualKw.trim();
        if (!s) return;
        const key = s.toLowerCase();
        if (mergedCustom.some((k) => k.toLowerCase() === key)) {
            toast.info("Already in the list");
            setManualKw("");
            return;
        }
        setCustomKeywords((prev) => [...prev, s]);
        setManualKw("");
    };

    const removeCustom = (kw) => {
        const key = kw.toLowerCase();
        // Remove from chip list if present
        setCustomKeywords((prev) => prev.filter((k) => k.toLowerCase() !== key));
        // Also strip from textarea if it's in there
        const remaining = parseKeywordText(customText).filter((k) => k.toLowerCase() !== key);
        setCustomText(remaining.join("\n"));
    };

    const negativesCount = (result?.ai?.negatives || []).length;
    const sitelinksCount = (result?.ai?.sitelinks || []).length;
    const calloutsCount = (result?.ai?.callouts || []).length;

    // Cascade-deduped pool sizes so the preview never diverges from the CSV.
    const pools = useMemo(() => {
        if (!result) return { customPool: [], seedPool: [], relatedPool: [], inferredPool: [] };
        const extraSeeds = extraSeedsText
            .split(/\r?\n/)
            .map((s) => s.trim())
            .filter(Boolean);
        return computeKeywordPools(result, { extraSeeds, customKeywords: mergedCustom });
    }, [result, extraSeedsText, mergedCustom]);

    const customCount = pools.customPool.length;
    const competitorCount = pools.competitorPool.length;
    const seedPoolCount = pools.seedPool.length;
    const relatedCount = pools.relatedPool.length;
    const inferredCount = pools.inferredPool.length;
    const nonEmptyGroups = [customCount, competitorCount, seedPoolCount, relatedCount, inferredCount].filter((n) => n > 0).length;
    const totalKeywords = customCount + competitorCount + seedPoolCount + relatedCount + inferredCount;
    const totalRows = totalKeywords * 2; // Phrase + Exact

    const canSubmit =
        businessName.trim().length > 0 &&
        websiteUrl.trim().length > 0 &&
        Number(dailyBudget) > 0;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!canSubmit) return;

        const extraSeeds = extraSeedsText
            .split(/\r?\n/)
            .map((s) => s.trim())
            .filter(Boolean);

        // Sanitize website URL: force https:// prefix
        let url = websiteUrl.trim();
        if (!/^https?:\/\//i.test(url)) url = `https://${url}`;

        try {
            const csv = buildCampaignCsv(result, {
                businessName: businessName.trim(),
                websiteUrl: url,
                dailyBudget: dailyBudget.trim(),
                extraSeeds,
                customKeywords: mergedCustom,
                trustCopy: {
                    headlines: trustCopy.headlines || [],
                    descriptions: trustCopy.descriptions || [],
                    callouts: trustCopy.callouts || [],
                },
            });
            downloadCsv(campaignFilename(result), csv);
            toast.success(
                `Campaign CSV downloaded — import via Google Ads Editor → File → Import CSV`
            );
            setOpen(false);
        } catch (e) {
            toast.error("Failed to build campaign CSV");
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button
                    data-testid="build-campaign-btn"
                    className="brutal-btn bg-ink border border-ink text-white font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5"
                    title="Build a launch-ready Google Ads campaign CSV"
                >
                    <RocketLaunch size={13} weight="bold" />
                    Build Campaign
                </button>
            </DialogTrigger>

            <DialogContent
                data-testid="campaign-builder-dialog"
                className="max-w-2xl rounded-none border-2 border-ink p-0 bg-white flex flex-col max-h-[90vh]"
            >
                <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
                    <DialogHeader className="p-6 border-b-2 border-ink shrink-0">
                        <DialogTitle className="font-display text-2xl font-black tracking-tighter text-ink">
                            Build Google Ads Campaign
                        </DialogTitle>
                        <DialogDescription className="mono-label mt-1">
                            One CSV · Campaign + up to 4 ad groups · Phrase + Exact ·
                            RSA per group · Custom keywords supported
                        </DialogDescription>
                    </DialogHeader>

                    <div
                        data-testid="campaign-builder-scroll"
                        className="p-6 space-y-5 overflow-y-auto thin-scroll flex-1 min-h-0"
                    >
                        {/* AI copy preview — shows exactly what will land in every RSA in the exported CSV.
                            Runs each value through sanitizeAdAsset so users see the SAME sanitized text
                            (typo labels stripped, mid-word chops recovered) that the CSV emits. */}
                        {(() => {
                            const rawHeads = result?.ai?.tagged_headlines?.length
                                ? result.ai.tagged_headlines
                                : (result?.ai?.headlines || []).map((h) => ({ text: h, tag: "" }));
                            const cleanedHeads = rawHeads
                                .map((row) => ({
                                    ...row,
                                    text: sanitizeAdAsset(row.text, 30),
                                }))
                                .filter((row) => row.text);
                            const cleanedDescs = (result?.ai?.descriptions || [])
                                .map((d) => sanitizeAdAsset(d, 90))
                                .filter(Boolean);
                            if (cleanedHeads.length === 0 && cleanedDescs.length === 0) return null;
                            return (
                            <div
                                data-testid="cb-ai-copy-preview"
                                className="border-2 border-klein bg-klein/5 p-4 space-y-3"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <div className="mono-label text-klein">
                                            Will Be Written Into Every Ad Group&apos;s RSA
                                        </div>
                                        <p className="text-xs text-neutral-700 mt-1">
                                            These 5 headlines + 3 descriptions land in every ad group in the CSV — no extra step required. Values are pre-validated: length ≤30/90, no placeholder labels, no mid-word chops.
                                        </p>
                                    </div>
                                </div>
                                {cleanedHeads.length > 0 && (
                                    <div>
                                        <div className="mono-label mb-1">
                                            Headlines ({cleanedHeads.length})
                                        </div>
                                        <ol className="space-y-1 text-sm text-ink">
                                            {cleanedHeads.map((row, i) => {
                                                const tagLabel = {
                                                    urgency: "URGENCY",
                                                    trust: "TRUST",
                                                    price: "PRICE",
                                                    local_keyword: "LOCAL + KW",
                                                }[row.tag] || "";
                                                return (
                                                    <li
                                                        key={i}
                                                        className="flex items-center gap-2 min-w-0"
                                                    >
                                                        <span className="mono-label shrink-0 w-5">{String(i + 1).padStart(2, "0")}</span>
                                                        {tagLabel && (
                                                            <span className="shrink-0 border border-klein bg-white text-klein font-mono uppercase text-[9px] tracking-widest font-bold px-1.5 py-0.5">
                                                                {tagLabel}
                                                            </span>
                                                        )}
                                                        <span className="font-mono truncate flex-1">{row.text}</span>
                                                        <span className="mono-label shrink-0">{row.text.length}/30</span>
                                                    </li>
                                                );
                                            })}
                                        </ol>
                                    </div>
                                )}
                                {cleanedDescs.length > 0 && (
                                    <div>
                                        <div className="mono-label mb-1">
                                            Descriptions ({cleanedDescs.length}) · each has a CTA
                                        </div>
                                        <ol className="space-y-1 text-sm text-ink">
                                            {cleanedDescs.map((d, i) => (
                                                <li key={i} className="flex items-start gap-2">
                                                    <span className="mono-label shrink-0 w-5 mt-0.5">
                                                        {String(i + 1).padStart(2, "0")}
                                                    </span>
                                                    <span className="font-mono text-xs leading-relaxed flex-1">
                                                        {d}
                                                    </span>
                                                    <span className="mono-label shrink-0 mt-0.5">{d.length}/90</span>
                                                </li>
                                            ))}
                                        </ol>
                                    </div>
                                )}
                                {result?.ai?.themed_ads?.length > 0 && (
                                    <p className="mono-label text-neutral-500 pt-1 border-t border-klein/20">
                                        Plus theme-specific headlines/descriptions per ad group ({result.ai.themed_ads.length} themes) also stacked into each RSA.
                                    </p>
                                )}
                            </div>
                            );
                        })()}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="mono-label block mb-1.5">
                                    Business name *
                                </label>
                                <input
                                    data-testid="cb-business-name"
                                    type="text"
                                    value={businessName}
                                    onChange={(e) => setBusinessName(e.target.value)}
                                    placeholder="Universal Water Purifier Solutions"
                                    required
                                    className="w-full h-11 px-3 border border-neutral-300 bg-white focus:border-klein focus:ring-0 text-base"
                                />
                            </div>
                            <div>
                                <label className="mono-label block mb-1.5">
                                    Daily budget *
                                </label>
                                <input
                                    data-testid="cb-daily-budget"
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={dailyBudget}
                                    onChange={(e) => setDailyBudget(e.target.value)}
                                    placeholder="1000"
                                    required
                                    className="w-full h-11 px-3 border border-neutral-300 bg-white focus:border-klein focus:ring-0 text-base"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="mono-label block mb-1.5">Website URL *</label>
                            <input
                                data-testid="cb-website-url"
                                type="text"
                                value={websiteUrl}
                                onChange={(e) => setWebsiteUrl(e.target.value)}
                                placeholder="www.universalwaterpurifiers.com"
                                required
                                className="w-full h-11 px-3 border border-neutral-300 bg-white focus:border-klein focus:ring-0 text-base"
                            />
                        </div>

                        <div>
                            <label className="mono-label block mb-1.5">
                                Additional seed keywords (optional · one per line)
                            </label>
                            <textarea
                                data-testid="cb-extra-seeds"
                                value={extraSeedsText}
                                onChange={(e) => setExtraSeedsText(e.target.value)}
                                placeholder={"ro water purifier services\nro water purifier amc"}
                                rows={3}
                                className="w-full px-3 py-2 border border-neutral-300 bg-white focus:border-klein focus:ring-0 text-sm font-mono resize-none"
                            />
                        </div>

                        {/* Custom keyword input — CSV upload, paste, or add-one — merged into a "Custom" ad group */}
                        <div className="border border-ink bg-neutral-50 p-4 space-y-3">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="mono-label">Custom keywords</div>
                                    <p className="text-xs text-neutral-600 mt-0.5">
                                        Upload CSV · paste · or add one. Deduped against seed / related / inferred.
                                    </p>
                                </div>
                                <span
                                    data-testid="cb-custom-count"
                                    className="font-heading text-lg font-black text-ink"
                                >
                                    {customCount}
                                </span>
                            </div>

                            <div className="flex gap-2">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".csv,.txt,text/csv,text/plain"
                                    onChange={handleFileUpload}
                                    className="hidden"
                                    data-testid="cb-custom-file"
                                />
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    data-testid="cb-custom-upload-btn"
                                    className="brutal-btn bg-white border border-ink text-ink font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5"
                                >
                                    <UploadSimple size={13} weight="bold" />
                                    Upload CSV / TXT
                                </button>
                                <div className="flex-1 flex gap-2">
                                    <input
                                        type="text"
                                        value={manualKw}
                                        onChange={(e) => setManualKw(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                addManualKeyword();
                                            }
                                        }}
                                        placeholder="Type a keyword and press Enter"
                                        data-testid="cb-custom-manual-input"
                                        className="flex-1 h-9 px-3 border border-neutral-300 bg-white focus:border-klein focus:ring-0 text-sm"
                                    />
                                    <button
                                        type="button"
                                        onClick={addManualKeyword}
                                        data-testid="cb-custom-add-btn"
                                        className="brutal-btn bg-ink border border-ink text-white font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1"
                                    >
                                        <Plus size={12} weight="bold" />
                                        Add
                                    </button>
                                </div>
                            </div>

                            <textarea
                                data-testid="cb-custom-paste"
                                value={customText}
                                onChange={(e) => setCustomText(e.target.value)}
                                placeholder={"Paste keywords — one per line or comma-separated\ne.g. ro water purifier repair\nkent ro service near me, aquaguard amc"}
                                rows={3}
                                className="w-full px-3 py-2 border border-neutral-300 bg-white focus:border-klein focus:ring-0 text-sm font-mono resize-none"
                            />

                            {mergedCustom.length > 0 && (
                                <div
                                    data-testid="cb-custom-chips"
                                    className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto thin-scroll border border-neutral-200 bg-white p-2"
                                >
                                    {mergedCustom.map((kw) => (
                                        <span
                                            key={kw}
                                            className="inline-flex items-center gap-1 bg-neutral-100 border border-neutral-300 pl-2 pr-1 py-0.5 text-xs font-mono text-ink"
                                        >
                                            {kw}
                                            <button
                                                type="button"
                                                onClick={() => removeCustom(kw)}
                                                aria-label={`Remove ${kw}`}
                                                className="hover:bg-neutral-300 rounded-sm p-0.5"
                                            >
                                                <X size={10} weight="bold" />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                            <KwCount label="Custom" list={{ length: customCount }} />
                            <KwCount label="Competitor" list={{ length: competitorCount }} />
                            <KwCount label="Seed" list={{ length: seedPoolCount }} />
                            <KwCount label="Related" list={{ length: relatedCount }} />
                            <KwCount label="Inferred" list={{ length: inferredCount }} />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            <KwCount label="Negatives" list={{ length: negativesCount }} />
                            <KwCount label="Sitelinks" list={{ length: sitelinksCount }} />
                            <KwCount label="Callouts" list={{ length: calloutsCount + (trustCopy.callouts?.length || 0) }} />
                        </div>

                        <TrustCopyPanel onChange={setTrustCopy} />

                        <div className="border border-ink bg-neutral-100 px-3 py-2 flex items-center justify-between">
                            <span className="mono-label">CSV will produce</span>
                            <span
                                data-testid="cb-row-preview"
                                className="font-heading text-sm font-bold text-ink"
                            >
                                1 campaign · {nonEmptyGroups} ad groups · {totalRows} keywords · RSAs per group · {negativesCount} negatives · {sitelinksCount} sitelinks · {calloutsCount + (trustCopy.callouts?.length || 0)} callouts
                            </span>
                        </div>
                    </div>

                    <DialogFooter className="p-6 border-t-2 border-ink flex items-center justify-between gap-3 shrink-0 bg-white">
                        <p className="mono-label max-w-xs">
                            Status: Paused · Bid: Manual CPC · Search network only
                        </p>
                        <button
                            data-testid="cb-download-btn"
                            type="submit"
                            disabled={!canSubmit}
                            className="brutal-btn bg-klein border border-klein text-white font-mono uppercase tracking-widest text-xs font-bold h-11 px-5 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <DownloadSimple size={14} weight="bold" />
                            Download Campaign CSV
                        </button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
